import React, { useRef, useState, useEffect } from "react";
import { Modal, Spin, Button, message, Input, List, Card, Tag, Select, Tabs, Image } from "antd";
import HomeRep from "../repository/homeRep";
import { API_BASE_URL } from "../helper";

const { Option } = Select;

export default function About() {
  const uploader = useRef();
  const [media, setMedia] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState("about");

  // Video Preview Modal State
  const [videoPreviewVisible, setVideoPreviewVisible] = useState(false);
  const [currentPreviewVideo, setCurrentPreviewVideo] = useState("");

  const [newMedia, setNewMedia] = useState({
    altText: "",
    section: "about",
    mediaList: [], // Array of { file, preview, type }
  });

  useEffect(() => {
    fetchMedia(activeSection);
  }, [activeSection]);

  const fetchMedia = async (section) => {
    setLoading(true);
    const response = await HomeRep.getAboutMedia(section);
    if (response?.error) {
      message.error("Failed to fetch media");
    } else {
      setMedia(response.data || []);
    }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) await handleFiles(files);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFiles = async (files) => {
    setUploading(true);
    const processedFiles = [];

    for (const file of files) {
      const mediaType = file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
        ? "video"
        : null;

      if (!mediaType) {
        message.warning(`Skipping ${file.name}: Unsupported file type.`);
        continue;
      }

      if (mediaType === "image" && file.size > 5242880) {
        message.warning(`Skipping ${file.name}: Image too big (max 5MB).`);
        continue;
      }

      if (mediaType === "video" && file.size > 52428800) {
        message.warning(`Skipping ${file.name}: Video too big (max 50MB).`);
        continue;
      }

      let preview;
      if (mediaType === "image") {
        preview = await getBase64(file);
      } else {
        preview = URL.createObjectURL(file);
      }

      processedFiles.push({ file, preview, type: mediaType, name: file.name });
    }

    setNewMedia((prev) => ({
      ...prev,
      mediaList: [...prev.mediaList, ...processedFiles],
    }));
    setUploading(false);
  };

  const removeSelectedFile = (index) => {
    setNewMedia((prev) => {
      const newList = [...prev.mediaList];
      const removed = newList.splice(index, 1)[0];
      if (removed.type === "video" && removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return { ...prev, mediaList: newList };
    });
  };

  function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const openModal = () => setIsModalOpen(true);

  const handleCancel = () => {
    setIsModalOpen(false);
    newMedia.mediaList.forEach((item) => {
      if (item.type === "video" && item.preview) URL.revokeObjectURL(item.preview);
    });
    setNewMedia({ altText: "", section: "about", mediaList: [] });
  };

  const handleOk = async () => {
    if (newMedia.mediaList.length === 0) {
      message.error("Please select at least one file to upload.");
      return;
    }
    setUploading(true);

    const isFile = true; // Use FormData
    const payload = {
      section: newMedia.section,
      altText: newMedia.altText,
      media: newMedia.mediaList.map((m) => m.file),
      mediaType: newMedia.mediaList[0].type,
    };

    const response = await HomeRep.addAboutMedia(payload, isFile);

    if (response?.error) {
      message.error(response.message || "Failed to upload media.");
    } else {
      message.success("Media uploaded successfully!");
      setIsModalOpen(false);
      newMedia.mediaList.forEach((item) => {
        if (item.type === "video" && item.preview) URL.revokeObjectURL(item.preview);
      });
      setNewMedia({ altText: "", section: "about", mediaList: [] });
      fetchMedia(activeSection);
    }
    setUploading(false);
  };

  const handleSetActive = async (mediaId) => {
    setLoading(true);
    const response = await HomeRep.setActiveAboutMedia({ mediaId });
    if (response?.error) {
      message.error(response.message || "Failed to set active media.");
    } else {
      message.success("Media set as active!");
      fetchMedia(activeSection);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    const response = await HomeRep.deleteMedia(id);
    if (response?.error) {
      message.error("Failed to delete media");
    } else {
      message.success("Media deleted successfully");
      fetchMedia(activeSection);
    }
    setLoading(false);
  };

  const openVideoPreview = (url) => {
    const fullUrl = url?.startsWith("data:") || url?.startsWith("http") ? url : `${API_BASE_URL}${url}`;
    setCurrentPreviewVideo(fullUrl);
    setVideoPreviewVisible(true);
  };

  const items = [
    { key: "about", label: "About Section" },
    { key: "clients", label: "Clients Section" },
  ];

  return (
    <section className="mt-md-3">
      <div className="page-header flex-md-row flex-column">
        <h1>Media</h1>
        <Button type="primary" className="primary-btn" onClick={openModal}>
          Add Media
        </Button>
      </div>

      <Tabs activeKey={activeSection} onChange={setActiveSection} items={items} />

      <Spin spinning={loading}>
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 5 }}
          dataSource={media}
          renderItem={(item) => (
            <List.Item>
              <Card
                cover={
                  item.mediaType === "image" ? (
                    <Image
                      alt={item.altText}
                      src={
                        item.url?.startsWith("data:") || item.url?.startsWith("http")
                          ? item.url
                          : `${API_BASE_URL}${item.url}`
                      }
                      style={{ height: 150, objectFit: "cover", width: "100%" }}
                    />
                  ) : (
                    <div style={{ position: "relative", cursor: "pointer" }} onClick={() => openVideoPreview(item.url)}>
                      <video
                        src={
                          item.url?.startsWith("data:") || item.url?.startsWith("http")
                            ? item.url
                            : `${API_BASE_URL}${item.url}`
                        }
                        style={{ height: 150, width: "100%", objectFit: "cover" }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(0,0,0,0.1)",
                        }}
                      >
                        <i className="fas fa-play-circle" style={{ fontSize: 40, color: "#fff", opacity: 0.8 }}></i>
                      </div>
                    </div>
                  )
                }
                actions={[
                  <Button type="link" onClick={() => handleSetActive(item._id)} disabled={item.isActive}>
                    {item.isActive ? "Active" : "Set Active"}
                  </Button>,
                  <Button type="link" danger onClick={() => handleDelete(item._id)}>
                    Delete
                  </Button>,
                ]}
              >
                <Card.Meta title={item.altText || "Media"} description={item.isActive ? <Tag color="green">Active</Tag> : null} />
              </Card>
            </List.Item>
          )}
        />
      </Spin>

      {/* Video Preview Modal */}
      <Modal
        open={videoPreviewVisible}
        onCancel={() => setVideoPreviewVisible(false)}
        footer={null}
        width={800}
        centered
        destroyOnClose
        bodyStyle={{ padding: 0, background: "#000" }}
      >
        <video src={currentPreviewVideo} controls autoPlay style={{ width: "100%", maxHeight: "80vh" }} />
      </Modal>

      <Modal
        title="Add Media"
        open={isModalOpen}
        onCancel={handleCancel}
        width={700}
        footer={[
          <Button key="cancel" onClick={handleCancel} disabled={uploading}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleOk}
            loading={uploading}
            disabled={newMedia.mediaList.length === 0}
          >
            Upload {newMedia.mediaList.length > 0 ? `(${newMedia.mediaList.length} files)` : ""}
          </Button>,
        ]}
        className="custom-modal"
      >
        <Spin spinning={uploading}>
          <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label>Section</label>
              <Select style={{ width: "100%" }} value={newMedia.section} onChange={(val) => setNewMedia((p) => ({ ...p, section: val }))}>
                <Option value="about">About</Option>
                <Option value="clients">Clients</Option>
              </Select>
            </div>
            <div style={{ flex: 2 }}>
              <label>Alt Text</label>
              <Input
                placeholder="Alt Text for all files"
                value={newMedia.altText}
                onChange={(e) => setNewMedia((prev) => ({ ...prev, altText: e.target.value }))}
              />
            </div>
          </div>

          <div
            className="custom-dragger p-0 w-100"
            style={{
              border: "2px dashed #aaa",
              borderRadius: 16,
              padding: 0,
              textAlign: "center",
              cursor: "pointer",
              minHeight: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              marginBottom: 16,
              background: "#fafbfc",
            }}
            onClick={() => uploader.current.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input type="file" accept="image/*,video/*" ref={uploader} onChange={handleFileChange} className="d-none" multiple />
            <div>
              <p style={{ color: "#888", fontSize: 15, margin: 0 }}>Click or drag & drop to upload (Multiple allowed)</p>
              <p style={{ color: "#888", fontSize: 12, margin: 0 }}>Max 5MB for images, 50MB for videos</p>
            </div>
          </div>

          {newMedia.mediaList.length > 0 && (
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              <List
                size="small"
                dataSource={newMedia.mediaList}
                renderItem={(item, index) => (
                  <List.Item
                    extra={
                      <Button type="text" danger onClick={() => removeSelectedFile(index)}>
                        Remove
                      </Button>
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        item.type === "image" ? (
                          <img src={item.preview} alt="prev" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }} />
                        ) : (
                          <video src={item.preview} style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4, background: "#000" }} />
                        )
                      }
                      title={item.name}
                      description={`${(item.file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                  </List.Item>
                )}
              />
            </div>
          )}
        </Spin>
      </Modal>
    </section>
  );
}
